/* src/views/jour/JourControleCtrl.jsx — L'ÉCRAN CONTRÔLE d'Aujourd'hui
   (lot 3) : le dessin de JourControleProto nourri des menage_runs TERMINÉS du
   mois, de leurs contrôles et incidents, des checklists (menage_log_info) et
   des photos signées. « Contrôler » bascule sur l'écran PARTAGÉ du contrôleur
   (CtrlControleEmbarque — aucune règle recopiée) ; « Confier » ouvre la
   feuille de désignation de l'accueil (CtrlSansPersonne — le même geste,
   jamais un second chemin). */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../../supabase'
import { todayParisIso } from '../MenageTodayView'
import { JourControleProto } from './proto/JourControleProto'
import { CtrlSansPersonne, CtrlControleEmbarque } from '../accueil/ControleursFeuilles'
import { etatOeil, etatIncident } from '../../lib/accueil-menages'
import { fetchLogInfo } from '../../lib/menage-log-info-cache'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import { chargerAnnuaireAgents } from '../../lib/accueil-api'

const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
const prenomDe = (email) => {
  const e = typeof email === 'string' ? email.trim() : ''
  if (!e) return null
  const p = e.split('@')[0]
  return p.charAt(0).toUpperCase() + p.slice(1)
}
const TIP_OEIL = { quelquun_dessus: 'ctrl_designe', personne_dessus: 'ctrl_personne',
  rien_a_signaler: 'ctrl_ras', a_reprendre: 'ctrl_reprendre',
  hors_fenetre: 'hors_fenetre' }

export default function JourControleCtrl({ cfg, currentUser, onNavigate, onDecision }) {
  const todayIso = useMemo(() => todayParisIso(), [])
  const [mois, setMois] = useState(() => todayParisIso().slice(0, 7))
  const [runs, setRuns] = useState(null)
  const [runsDuJour, setRunsDuJour] = useState([])
  const [annuaire, setAnnuaire] = useState([])
  const [controles, setControles] = useState({})
  const [incidents, setIncidents] = useState([])
  const [pieces, setPieces] = useState({})     // logement_id -> pieces cfg
  const [erreur, setErreur] = useState(null)
  const [feuille, setFeuille] = useState(null) // { confier: run } | { embarque: runId }
  // La VISIONNEUSE : les photos d'un run se SIGNENT AU CLIC (jamais en masse —
  // correction 27/08 : le préchargement faisait une requête PAR ménage).
  const [visio, setVisio] = useState(null)     // { runId, photos: null|[..], index }

  const charger = useCallback(() => {
    ;(async () => {
      try {
        const debut = `${mois}-01`
        const [an, m] = mois.split('-').map(Number)
        const fin = `${m === 12 ? an + 1 : an}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`
        const { data, error } = await supabase.from('menage_runs')
          .select('id, logement_id, scheduled_date, status, assigned_provider_email, controleur_designe_email, completed_at, items_checked, photos')
          .gte('scheduled_date', debut).lt('scheduled_date', fin)
          .eq('status', 'completed')
          .order('scheduled_date', { ascending: false })
        if (error) throw error
        setRuns(data || []); setErreur(null)
        // LA CHARGE DU JOUR des agents (correction 27/08 : la même feuille la
        // montre à l'accueil, elle doit la montrer partout) — les runs
        // d'AUJOURD'HUI, tous statuts, une petite lecture de plus.
        const { data: duJour } = await supabase.from('menage_runs')
          .select('id, logement_id, status, assigned_provider_email')
          .eq('scheduled_date', todayParisIso()).neq('status', 'skipped')
        setRunsDuJour(duJour || [])
        const ids = (data || []).map((r) => r.id)
        if (ids.length) {
          const { data: ctrls } = await supabase.from('menage_controles')
            .select('id, run_id, statut, controleur_email, cree_le, decide_le')
            .in('run_id', ids)
          const m2 = {}
          for (const c of ctrls || []) m2[c.run_id] = c
          setControles(m2)
          const { data: incs } = await supabase.from('incidents')
            .select('id, run_id, status').in('run_id', ids)
          setIncidents(incs || [])
        } else { setControles({}); setIncidents([]) }
      } catch (e) {
        console.error('[jour] contrôle', e?.message || e)
        setErreur('La lecture des ménages a échoué.')
      }
    })()
  }, [mois])
  useEffect(() => { const id = setTimeout(charger, 0); return () => clearTimeout(id) }, [charger])
  useEffect(() => {
    let vivant = true
    chargerAnnuaireAgents(supabase).then((a) => { if (vivant) setAnnuaire(a) })
    return () => { vivant = false }
  }, [])

  // Les checklists : UNE lecture par LOGEMENT (≤ 10, en cache) — jamais par
  // run. Les photos ne se chargent PAS ici : la carte montre les tuiles par
  // pièce (run.photos, déjà dans la ligne), et la signature part AU CLIC.
  useEffect(() => {
    let vivant = true
    const vus = new Set()
    for (const r of runs || []) {
      if (vus.has(r.logement_id) || (r.logement_id in pieces)) continue
      vus.add(r.logement_id)
      fetchLogInfo(r.logement_id, r.scheduled_date)
        .then((info) => { if (vivant) setPieces((p) => ({ ...p,
          [r.logement_id]: (info && info.menage && info.menage.pieces) || [] })) })
        .catch(() => { if (vivant) setPieces((p) => ({ ...p, [r.logement_id]: [] })) })
    }
    return () => { vivant = false }
  }, [runs])   // eslint-disable-line react-hooks/exhaustive-deps

  // Le clic sur une tuile : UNE requête, pour CE run, puis la visionneuse.
  const ouvrirVisio = useCallback(async (runId, index) => {
    setVisio({ runId, photos: null, index })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`/api/admin-users?action=menage_run_photos&run_id=${encodeURIComponent(runId)}`,
        { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {} })
      const json = await res.json().catch(() => ({}))
      setVisio((v) => (v && v.runId === runId
        ? { ...v, photos: res.ok && Array.isArray(json?.photos) ? json.photos : [] } : v))
    } catch (_) {
      setVisio((v) => (v && v.runId === runId ? { ...v, photos: [] } : v))
    }
  }, [])

  const parLogement = useMemo(() => {
    const m = {}
    for (const l of getAllLogsFlat(cfg)) m[l.id] = l
    return m
  }, [cfg])
  const nomLogement = useCallback((id) => (parLogement[id] && parLogement[id].nom) || id,
    [parLogement])
  // LE roster de l'accueil : adresses vues sur la fenêtre + la CHARGE DU JOUR
  // (occupé = un ménage du jour pas terminé) — même dérivation, même phrase.
  const optionsAgents = useMemo(() => {
    const vus = new Set(); const opts = []
    const emails = [...new Set([...(annuaire || []),
      ...[...(runs || []), ...runsDuJour]
        .map((r) => r.assigned_provider_email).filter(Boolean)])]
    for (const e of emails) {
      if (vus.has(e)) continue
      vus.add(e)
      const p = prenomDe(e)
      const duJour = runsDuJour.filter((x) => x.assigned_provider_email === e)
      const restants = duJour.filter((x) => x.status !== 'completed')
      opts.push({
        value: e, label: p,
        occupe: restants.length > 0,
        charge: duJour.length === 0 ? 'rien ce jour'
          : `${duJour.length} ménage${duJour.length > 1 ? 's' : ''} · ${
            duJour.map((x) => nomLogement(x.logement_id)).join(', ')}`,
      })
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label, 'fr'))
  }, [runs, runsDuJour, nomLogement, annuaire])

  const heureLoc = (iso) => {
    const d = iso ? new Date(iso) : null
    if (!d || Number.isNaN(d.getTime())) return null
    const [hh, mm] = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit',
      minute: '2-digit', hour12: false, timeZone: 'Europe/Paris' }).format(d).split(':')
    return mm === '00' ? `${Number(hh)}h` : `${Number(hh)}h${mm}`
  }

  const cartes = useMemo(() => (runs || []).map((r) => {
    const ctrl = controles[r.id]
    const oeil = etatOeil(r, ctrl, { todayIso })
    const inc = etatIncident(r, incidents)
    const pcs = pieces[r.logement_id] || []
    const checked = r.items_checked && typeof r.items_checked === 'object' ? r.items_checked : {}
    const nbCoches = Object.values(checked).reduce((n, l) => n + (Array.isArray(l) ? l.length : 0), 0)
    const nbTotal = pcs.reduce((n, pc) => n + ((pc.items || []).length), 0)
    // Les tuiles viennent de run.photos (le compte par pièce est DANS la
    // ligne) — l'image elle-même se signe au clic, jamais avant.
    const brutes = Array.isArray(r.photos) ? r.photos : []
    const parPiece = {}
    for (const ph of brutes) {
      const cle = (ph && ph.piece_id) || '__sans__'
      parPiece[cle] = (parPiece[cle] || 0) + 1
    }
    const bandePhotos = pcs.map((pc) => ({
      piece: pc.name || pc.id, n: parPiece[pc.id] || 0, url: null,
    })).filter((t) => t.n > 0 || pcs.length <= 6)
    const dejaControle = ctrl && ctrl.statut && ctrl.statut !== 'en_cours'
    return {
      testId: `jour-controle-${r.id}`,
      logement: nomLogement(r.logement_id),
      sous: [prenomDe(r.assigned_provider_email) || 'personne',
        r.completed_at ? `terminé à ${heureLoc(r.completed_at)}` : null,
        r.scheduled_date !== todayIso ? r.scheduled_date : null]
        .filter(Boolean).join(' · '),
      tips: [
        ...(inc ? [{ variante: inc === 'a_juger' ? 'incident_a_juger' : 'incident_regle' }] : []),
        { variante: 'valide' },
        ...(TIP_OEIL[oeil] ? [{ variante: TIP_OEIL[oeil] }] : []),
      ],
      checklist: nbTotal
        ? (nbCoches >= nbTotal ? `Checklist validée · ${nbCoches} sur ${nbTotal}`
          : `Checklist · ${nbCoches} sur ${nbTotal}`)
        : 'Aucune checklist configurée',
      tagChecklist: nbTotal && nbCoches < nbTotal
        ? `${nbTotal - nbCoches} non cochée${nbTotal - nbCoches > 1 ? 's' : ''}` : null,
      photos: bandePhotos,
      onPhoto: (ph, idx) => ouvrirVisio(r.id, idx || 0),
      onControler: !dejaControle && oeil !== 'hors_fenetre'
        ? () => setFeuille({ embarque: r.id }) : null,
      onConfier: !dejaControle && oeil !== 'hors_fenetre'
        ? () => setFeuille({ confier: r }) : null,
    }
  }), [runs, controles, incidents, pieces, todayIso, nomLogement, ouvrirVisio])

  const stats = useMemo(() => {
    const etats = (runs || []).map((r) => etatOeil(r, controles[r.id], { todayIso }))
    return {
      controles: etats.filter((e) => e === 'rien_a_signaler').length,
      aReprendre: etats.filter((e) => e === 'a_reprendre').length,
      aControler: etats.filter((e) => e === 'quelquun_dessus' || e === 'personne_dessus').length,
    }
  }, [runs, controles, todayIso])

  const [an, moisN] = mois.split('-').map(Number)
  const moisCourant = todayIso.slice(0, 7)

  return (
    <>
      {runs === null && !erreur && <div className="page"><p className="note">On lit les ménages…</p></div>}
      {erreur && <div className="page"><p className="note" role="alert"
        style={{ color: '#8f2f1d' }}>{erreur}</p></div>}
      {runs !== null && (
        <JourControleProto
          mois={{
            label: `${MOIS_LONGS[moisN - 1].charAt(0).toUpperCase()}${MOIS_LONGS[moisN - 1].slice(1)} ${an}`,
            n: (runs || []).length,
            onPrec: () => setMois(moisN === 1 ? `${an - 1}-12` : `${an}-${String(moisN - 1).padStart(2, '0')}`),
            onSuiv: () => setMois(moisN === 12 ? `${an + 1}-01` : `${an}-${String(moisN + 1).padStart(2, '0')}`),
            suivActif: mois < moisCourant,
          }}
          stats={stats}
          cartes={cartes}
          enfantsApres={cartes.length === 0
            ? <p className="note">Aucun ménage terminé ce mois — rien à contrôler ici.</p>
            : null} />
      )}
      {feuille && feuille.confier && (
        <CtrlSansPersonne
          ligne={{ runId: feuille.confier.id,
            titre: nomLogement(feuille.confier.logement_id),
            sousTitre: nomLogement(feuille.confier.logement_id), cle: 'jour' }}
          run={feuille.confier}
          optionsAgents={optionsAgents}
          onFermer={() => setFeuille(null)}
          onDecide={() => { setFeuille(null); charger(); onDecision && onDecision() }}
          onMoiMeme={() => setFeuille({ embarque: feuille.confier.id })} />
      )}
      {visio && (
        <div role="dialog" aria-modal="true" data-testid="controle-visio"
          onClick={() => setVisio(null)}
          style={{ position: 'fixed', inset: 0, background: '#233240', zIndex: 9500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap', gap: 10, padding: 18, overflowY: 'auto',
            cursor: 'pointer' }}>
          {visio.photos === null && (
            <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 13 }}>On signe les photos…</p>
          )}
          {visio.photos && visio.photos.length === 0 && (
            <p style={{ color: 'rgba(255,255,255,.8)', fontSize: 13 }}>Aucune photo lisible pour ce ménage.</p>
          )}
          {(visio.photos || []).map((ph, i) => (
            <img key={i} src={ph.signed_url || ph.url} alt={`Prise pendant le ménage (${i + 1})`}
              style={{ maxWidth: 'min(94vw, 520px)', maxHeight: '82vh', borderRadius: 10 }} />
          ))}
        </div>
      )}
      {feuille && feuille.embarque && (
        <CtrlControleEmbarque runId={feuille.embarque} cfg={cfg}
          currentUser={currentUser}
          onFermer={() => { setFeuille(null); charger(); onDecision && onDecision() }} />
      )}
    </>
  )
}
